import React from 'react';
import { Box, Tooltip, Typography, Zoom } from '@mui/material';
import { makeStyles } from '@mui/styles';

import DescTooltip from '../DescTooltip';
import CowbellFactorsMapping from '../../utility/CowbellFactorsMapping';
import {
  DetailedCFRangeGraph,
  DetailedNonLiveCFRangeGraph,
} from '../graphs/DetailedCFRangeGraph';

export interface CowbellFactorDatum {
  readonly factorKey?: string;
  readonly company: number;
  readonly min: number;
  readonly max: number;
  readonly industry: number;
}

interface CowbellFactorProps {
  readonly factor: CowbellFactorDatum;
  readonly latestRiskSelected: boolean;
}

/** The single range-graph primitive: live (blue) or non-live (grey). */
export function CowbellFactorGraph({
  factor,
  latestRiskSelected,
}: Readonly<CowbellFactorProps>) {
  const Graph = latestRiskSelected
    ? DetailedCFRangeGraph
    : DetailedNonLiveCFRangeGraph;
  return (
    <Graph
      min={factor.min}
      max={factor.max}
      factor={factor.company}
      average={factor.industry}
    />
  );
}

/** One CF row: name (with tooltip) + its range graph. Used for every factor. */
export function CowbellFactor({
  factor,
  latestRiskSelected,
}: Readonly<CowbellFactorProps>) {
  const classes = useFactorNameTooltipStyles();
  const meta = CowbellFactorsMapping[factor.factorKey ?? ''] ?? {};
  return (
    <Box
      mb={2}
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
    >
      <Box width="50%">
        <Box display="flex" alignItems="center">
          {/* Description shown as a tooltip on the CF name to save vertical
              space; the ⓘ icon keeps its longer definition. `describeChild`
              makes the subtext an accessible *description* rather than
              replacing the CF name; `tabIndex` keeps it keyboard-reachable. */}
          <Tooltip
            title={meta.subtext}
            describeChild
            placement="top"
            arrow
            classes={{ tooltip: classes.tooltip }}
            TransitionComponent={Zoom}
          >
            <Typography
              tabIndex={0}
              style={{ width: 'fit-content', cursor: 'default' }}
            >
              {meta.label}
            </Typography>
          </Tooltip>
          <DescTooltip tooltip={meta.tooltip} />
        </Box>
      </Box>
      <Box width="40%" style={{ fontWeight: 600 }}>
        <CowbellFactorGraph
          factor={factor}
          latestRiskSelected={latestRiskSelected}
        />
      </Box>
    </Box>
  );
}

// Matches the DescTooltip (ⓘ) bubble so the CF-name tooltip and the icon
// tooltip look identical.
const useFactorNameTooltipStyles = makeStyles(({ palette }: any) => ({
  tooltip: {
    border: `1px solid ${palette.common.cowbellBlue}`,
    backgroundColor: palette.background.default,
    color: palette.text.primary,
    borderRadius: 5,
    padding: '1rem',
    fontSize: '0.875rem',
  },
}));
